import stockVoices from "../data/stockVoices.json";
import portraitSources from "../data/voiceAvatars.json";

export type VoiceGender = "female" | "male" | "neutral" | "unknown";
export type VoiceUseCase = "advertisement" | "informative_educational" | "narrative_story" | "social_media";
export type VoiceSort = "recent" | "az" | "za";
export type VoiceSample = { language: string; url: string };
export type LibraryVoice = {
  value: string; label: string; key: string; gender: VoiceGender;
  description: string; useCases: string[]; samples: VoiceSample[];
  portrait?: string; language?: string; accent?: string; recommended?: boolean;
};
export type VoicePreferences = { liked: string[]; recent: Record<string, number> };
export const EMPTY_VOICE_PREFERENCES: VoicePreferences = { liked: [], recent: {} };

// Editorial starting points based on Google's published voice characteristics.
// These suggestions do not restrict the voice or alter the generation request.
const googleUseCases: Record<VoiceUseCase, string[]> = {
  advertisement: ["Alnilam", "Kore", "Orus", "Pulcherrima"],
  informative_educational: ["Charon", "Iapetus", "Erinome", "Rasalgethi", "Sadaltager", "Schedar"],
  narrative_story: ["Achernar", "Algenib", "Algieba", "Despina", "Enceladus", "Gacrux", "Sulafat", "Vindemiatrix"],
  social_media: ["Achird", "Aoede", "Autonoe", "Callirrhoe", "Fenrir", "Laomedeia", "Leda", "Puck", "Sadachbia", "Umbriel", "Zephyr", "Zubenelgenubi"],
};
export type VoiceMetadata = { gender: string; description: string; useCases: string[]; samples: VoiceSample[]; language?: string; accent?: string; portrait?: string };
const metadata: Record<string, VoiceMetadata> = stockVoices;
// Individual portrait assignments stay stable when the provider catalog is reordered.
const portraits: Record<string, string> = portraitSources;
function elevenVoiceID(sample: VoiceSample): string | undefined {
  try { return new URL(sample.url).pathname.match(/\/voices\/([^/]+)/)?.[1]; } catch { return undefined; }
}
function providerSamples(provider: string, samples: VoiceSample[]): VoiceSample[] {
  if (provider !== "elevenlabs" || samples.length < 2) return samples;
  const id = elevenVoiceID(samples[0]);
  return id ? samples.filter(sample => elevenVoiceID(sample) === id) : samples;
}
export function libraryVoices(model: string, options: { value: string; label: string }[], additional: Record<string, VoiceMetadata> = {}): LibraryVoice[] {
  const provider = model.startsWith("Gemini") ? "google" : model.startsWith("Eleven") ? "elevenlabs" : model;
  return options.map(option => {
    const key = `${provider}:${option.label.toLowerCase()}`;
    const data = metadata[key] ?? additional[key];
    const samples = providerSamples(provider, data?.samples ?? []);
    return { ...option, key: data ? key : `${model}:${option.value}`, gender: (data?.gender ?? "unknown") as VoiceGender,
      description: data?.description ?? "Stock voice", samples,
      portrait: portraits[key] ?? data?.portrait, language: data?.language, accent: data?.accent,
      recommended: Boolean(samples.length),
      useCases: provider === "google" ? Object.entries(googleUseCases).filter(([, names]) => names.includes(option.label)).map(([useCase]) => useCase) : data?.useCases ?? [],
    };
  });
}
// Supplier labels often append delivery traits to the name. Keep the full label
// as the stable identity, but give the name and traits their own space in the UI.
export function voiceDisplayText(voice: Pick<LibraryVoice, "label" | "description">): { name: string; description: string } {
  const parts = voice.label.match(/^(.+?)\s*[-–—]\s+(.+)$/u);
  return {
    name: parts?.[1].trim() ?? voice.label,
    description: (parts?.[2] ?? (voice.description === "Stock voice" ? "" : voice.description)).replace(/\s+/g, " ").trim(),
  };
}

export function voiceAccentName(accent: string): string {
  return accent.replaceAll("_", " ").replace(/\b\p{L}/gu, letter => letter.toLocaleUpperCase());
}
const languageNames = new Intl.DisplayNames(["en"], { type: "language" });
export function previewLanguageName(code: string): string {
  try { return languageNames.of(code) ?? code; } catch { return code; }
}
const languageRegions: Record<string, string> = {
  af: "ZA", am: "ET", ar: "SA", as: "IN", az: "AZ", be: "BY", bg: "BG", bn: "BD", bs: "BA",
  ca: "ES", ceb: "PH", cs: "CZ", cy: "GB", da: "DK", de: "DE", el: "GR", en: "US", es: "ES", et: "EE",
  eu: "ES", fa: "IR", fi: "FI", fil: "PH", fr: "FR", ga: "IE", gl: "ES", gu: "IN", ha: "NG",
  he: "IL", hi: "IN", hr: "HR", hu: "HU", hy: "AM", id: "ID", is: "IS", it: "IT", ja: "JP",
  jv: "ID", ka: "GE", kk: "KZ", km: "KH", kn: "IN", ko: "KR", ky: "KG", lb: "LU", ln: "CD", lo: "LA", lt: "LT",
  lv: "LV", mk: "MK", ml: "IN", mn: "MN", mr: "IN", ms: "MY", mt: "MT", my: "MM", ne: "NP",
  nl: "NL", no: "NO", ny: "MW", or: "IN", pa: "IN", pl: "PL", ps: "AF", pt: "PT", ro: "RO", ru: "RU",
  sd: "PK", si: "LK", sk: "SK", sl: "SI", so: "SO", sq: "AL", sr: "RS", su: "ID", sv: "SE",
  sw: "KE", ta: "IN", te: "IN", th: "TH", tl: "PH", tr: "TR", uk: "UA", ur: "PK", uz: "UZ",
  vi: "VN", zh: "CN", zu: "ZA",
};
const accentRegions: Record<string, string> = {
  american: "US", british: "GB", "received pronunciation": "GB", australian: "AU", canadian: "CA",
  irish: "IE", scottish: "GB", welsh: "GB", indian: "IN", nigerian: "NG", "south african": "ZA",
  "new zealand": "NZ", mexican: "MX", argentinian: "AR", colombian: "CO", chilean: "CL",
  peruvian: "PE", venezuelan: "VE", brazilian: "BR", quebec: "CA", taiwanese: "TW",
};
export function voiceLanguageFlag(code: string, accent?: string): string {
  const parts = code.toLowerCase().split(/[-_]/);
  const explicitRegion = parts.slice(1).find(part => part.length === 2)?.toUpperCase();
  const region = (accent && accentRegions[accent.toLowerCase().replaceAll("_", " ")]) || explicitRegion || languageRegions[parts[0]];
  return region ? String.fromCodePoint(...[...region].map(letter => 0x1F1E6 + letter.charCodeAt(0) - 65)) : "";
}
export function filterLibraryVoices(voices: LibraryVoice[], preferences: VoicePreferences, filters: {
  search: string; gender: string; liked: boolean; useCase: string; sort: VoiceSort; language?: string; accent?: string;
}): LibraryVoice[] {
  const search = filters.search.trim().toLocaleLowerCase();
  return voices.filter(voice => (!filters.gender || voice.gender === filters.gender)
    && (!filters.language || voice.language === filters.language || voice.samples.some(sample => sample.language === filters.language))
    && (!filters.accent || voice.accent === filters.accent)
    && (!filters.liked || preferences.liked.includes(voice.key))
    && (!filters.useCase || voice.useCases.includes(filters.useCase))
    && (!search || `${voice.label} ${voice.description} ${voice.accent ?? ""} ${voice.language ? previewLanguageName(voice.language) : ""} ${voice.samples.map(sample => previewLanguageName(sample.language)).join(" ")}`.toLocaleLowerCase().includes(search)))
    .sort((a, b) => {
      if (filters.sort === "recent") {
        const recent = (preferences.recent[b.key] ?? 0) - (preferences.recent[a.key] ?? 0);
        if (recent) return recent;
      }
      return a.label.localeCompare(b.label) * (filters.sort === "za" ? -1 : 1);
    });
}
export function parseVoicePreferences(raw: string | null): VoicePreferences {
  try {
    const value: unknown = JSON.parse(raw ?? "null");
    if (!value || typeof value !== "object") return EMPTY_VOICE_PREFERENCES;
    const { liked, recent } = value as Partial<VoicePreferences>;
    return { liked: Array.isArray(liked) ? [...new Set(liked.filter((key): key is string => typeof key === "string"))].slice(0, 500) : [],
      recent: recent && typeof recent === "object" && !Array.isArray(recent) ? Object.fromEntries(Object.entries(recent).filter(([, time]) => typeof time === "number" && Number.isFinite(time) && time > 0).slice(0, 500)) : {} };
  } catch { return EMPTY_VOICE_PREFERENCES; }
}
